import type {
  ApolloQueryResult,
  DocumentNode,
  FetchPolicy,
  OperationVariables,
} from "@apollo/client";
import { print } from "graphql";

import { createRequestId, getOperationName } from "@/client/graphql/requestMeta";
// eslint-disable-next-line import/no-restricted-paths
import { analysisEventKey, trackAnalysisEvent } from "@/utils/analysis/event";
import { client } from "./";

type AnyJson = Record<string, unknown>;

export const createQueryHandler = <
  TVariables extends OperationVariables = AnyJson,
  VResponse = AnyJson,
>(
  documentNode: DocumentNode,
  fetchPolicy?: FetchPolicy,
): ((variables: TVariables) => Promise<VResponse>) => {
  return (variables) => {
    const startTime = performance.now();
    const requestId = createRequestId();
    const operationName = getOperationName(documentNode);

    return client
      .query({
        query: documentNode,
        variables,
        fetchPolicy: fetchPolicy,
        context: {
          requestId,
        },
      })
      .then(
        (
          res: ApolloQueryResult<VResponse> & {
            extensions?: { retryCount?: number };
          },
        ) => {
          if (res.errors || !res.data) {
            console.error(res.errors);
            throw new Error("An error occurred while fetching data");
          }

          // レイテンシの計算
          const endTime = performance.now();
          const duration = endTime - startTime;
          const retryCount =
            typeof res.extensions?.retryCount === "number" ? res.extensions.retryCount : 0;

          // analysis event の記録
          trackAnalysisEvent(analysisEventKey.GRAPHQL_QUERY_SUCCEEDED, {
            operationName,
            query: print(documentNode),
            variables,
            duration,
            retryCount,
            requestId,
            fetchPolicy,
          });

          return res.data;
        },
      )
      .catch((error) => {
        const endTime = performance.now();
        const duration = endTime - startTime;

        // analysis event の記録
        trackAnalysisEvent(analysisEventKey.GRAPHQL_QUERY_FAILED, {
          operationName,
          query: print(documentNode),
          variables,
          error: error.message,
          duration,
          requestId,
        });

        console.error(
          "GraphQL Query Failure:\n" +
            `  ${JSON.stringify(documentNode)}` +
            "\n" +
            "\nwith\n" +
            `  ${JSON.stringify(variables)} ` +
            "\n" +
            "\nis return\n" +
            `  ${JSON.stringify(error.message)}`,
        );

        throw error;
      });
  };
};

export const createNoVarsQueryHandler = <VResponse = AnyJson>(
  documentNode: DocumentNode,
  fetchPolicy?: FetchPolicy,
): (() => Promise<VResponse>) => {
  const handler = createQueryHandler<{}, VResponse>(documentNode, fetchPolicy);
  return () => handler({});
};
